import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMap } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import { useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import Supercluster from 'supercluster';
import PinCard from './PinCard';
import SidePanel from './SidePanel';
import PopupContent from './PopupContent';
import { parseMetadata } from '../utils/metadataParser';

// Fix for default markers in react-leaflet
delete L.Icon.Default.prototype._getIconUrl;

// Use Leaflet's default marker but with red color - smaller size
const customIcon = new L.Icon({
  iconUrl: 'https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-red.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/0.7.7/images/marker-shadow.png',
  iconSize: [15, 28],
  iconAnchor: [8, 28],
  popupAnchor: [1, -28],
  shadowSize: [15, 28],
});

// Create user location marker icon
const userLocationIcon = L.divIcon({
  className: 'user-location-marker',
  html: `<div style="
    width: 16px;
    height: 16px;
    background: #4285f4;
    border: 3px solid white;
    border-radius: 50%;
    box-shadow: 0 2px 6px rgba(0,0,0,0.3);
    position: relative;
  ">
    <div style="
      position: absolute;
      width: 40px;
      height: 40px;
      background: rgba(66, 133, 244, 0.2);
      border-radius: 50%;
      top: -15px;
      left: -15px;
      animation: pulse 2s infinite;
    "></div>
  </div>`,
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});

// Create cluster icon
const createClusterIcon = (count) => {
  const size = 
    count < 10 ? 35 :
    count < 25 ? 40 :
    45;

  return L.divIcon({
    html: `<div style="
      background: #dc3545;
      border: 2px solid #c82333;
      border-radius: 50%;
      color: white;
      width: ${size}px;
      height: ${size}px;
      display: flex;
      align-items: center;
      justify-content: center;
      font-weight: bold;
      font-size: 14px;
      box-shadow: 0 2px 6px rgba(0,0,0,0.3);
    ">${count}</div>`,
    iconSize: [size, size],
    iconAnchor: [size / 2, size / 2],
    className: 'marker-cluster'
  });
};

// Component to handle clusters inside MapContainer context
const ClusterLayer = React.memo(({ clusters, pins, zoom, onMarkerClick, onMarkerHover, isMobile, onClusterClick, onPopupExplore }) => {
  const map = useMap();
  
  // Handle cluster click with map instance
  const handleClusterClickInternal = (cluster) => {
    const expansionZoom = Math.min(
      onClusterClick(cluster), // Get expansion zoom from parent
      18
    );
    
    const [lng, lat] = cluster.geometry.coordinates;
    console.log('Zooming to cluster with map:', { lat, lng, expansionZoom, hasMap: !!map });
    
    // Use the map instance directly
    map.flyTo([lat, lng], expansionZoom, {
      duration: 0.4,
      easeLinearity: 0.1
    });
  };

  return (
    <>
      {clusters.map((cluster) => {
        const { cluster: isCluster, point_count: pointCount } = cluster.properties;
        const [lng, lat] = cluster.geometry.coordinates;

        if (isCluster) {
          return (
            <Marker
              key={`cluster-${cluster.id}`}
              position={[lat, lng]}
              icon={createClusterIcon(pointCount)}
              eventHandlers={{
                click: (e) => {
                  e.originalEvent.stopPropagation();
                  handleClusterClickInternal(cluster);
                },
              }}
            />
          );
        }

        // Single pin
        const pin = pins.find(p => p.id === cluster.properties.pinId);
        if (!pin) return null;
        
        if (isMobile) {
          return (
            <Marker
              key={`${pin.id}`}
              position={[pin.lat, pin.lng]}
              icon={customIcon}
              eventHandlers={{
                click: (e) => {
                  e.originalEvent.stopPropagation();
                  onMarkerClick(pin);
                },
              }}
            >
              <Popup closeButton={true} className="mobile-popup">
                <PopupContent pin={pin} onExplore={onPopupExplore} />
              </Popup>
            </Marker>
          );
        } else {
          return (
            <Marker
              key={`${pin.id}`}
              position={[pin.lat, pin.lng]}
              icon={customIcon}
              eventHandlers={{
                mouseover: (e) => onMarkerHover(pin, e),
                click: (e) => {
                  e.originalEvent.stopPropagation();
                  onMarkerClick(pin);
                },
              }}
            />
          );
        }
      })}
    </>
  );
});

// Component to handle map events
const MapEventHandler = React.memo(({ onMapClick, onViewportChange }) => {
  const map = useMapEvents({
    click: onMapClick,
    zoomend: () => {
      if (onViewportChange) {
        onViewportChange(map);
      }
    },
    moveend: () => {
      if (onViewportChange) {
        onViewportChange(map);
      }
    },
  });
  
  React.useEffect(() => {
    // Initial viewport change
    if (onViewportChange && map) {
      onViewportChange(map);
    }
  }, [map]);

  return null;
});

const MontrealMap = ({ pins = [] }) => {
  const navigate = useNavigate();
  const mapRef = useRef(null);
  const [hoverTimeout, setHoverTimeout] = useState(null);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [hoveredPin, setHoveredPin] = useState(null); // For side panel
  const [userLocation, setUserLocation] = useState(null); // User's current location
  const [locationError, setLocationError] = useState(null); // Geolocation error
  const [clusters, setClusters] = useState([]);
  const [zoom, setZoom] = useState(13);
  const [bounds, setBounds] = useState(null);

  // Initialize supercluster and memoize converted pins
  const supercluster = useMemo(() => {
    return new Supercluster({
      radius: 60, // Cluster radius in pixels (smaller = less aggressive clustering)
      maxZoom: 16, // Max zoom to cluster points on
      minPoints: 2, // Minimum points to form a cluster
      extent: 512, // Larger tile extent for better performance
      nodeSize: 32, // Smaller KD-tree leaf node for faster queries
    });
  }, []);

  // Convert pins to GeoJSON and cache them
  const geoJsonPoints = useMemo(() => {
    return pins.map(pin => ({
      type: 'Feature',
      properties: {
        cluster: false,
        pinId: pin.id,
        ...pin
      },
      geometry: {
        type: 'Point',
        coordinates: [pin.lng, pin.lat]
      }
    }));
  }, [pins]);

  // Load points into supercluster only when pins change
  useEffect(() => {
    if (geoJsonPoints.length > 0) {
      supercluster.load(geoJsonPoints);
    }
  }, [geoJsonPoints, supercluster]);

  // Update clusters when bounds or zoom changes
  useEffect(() => {
    if (!bounds || !geoJsonPoints.length) return;

    // Get clusters for current bounds and zoom
    const bbox = [
      bounds.getWest(),
      bounds.getSouth(),
      bounds.getEast(),
      bounds.getNorth()
    ];

    const clustersData = supercluster.getClusters(bbox, zoom);
    setClusters(clustersData);
  }, [bounds, zoom, supercluster, geoJsonPoints]);

  // Add window resize listener
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Cleanup hover timeout on unmount
  useEffect(() => {
    return () => {
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
    };
  }, [hoverTimeout]);


  // Le Plateau coordinates (Plateau-Mont-Royal)
  const plateauCenter = [45.5200, -73.5800];
  
  // Center map for all devices
  const getMapCenter = () => {
    return plateauCenter; // Same center for all devices now
  };
  
  // Montreal bounds - roughly the metropolitan area
  const montrealBounds = [
    [45.4, -74.0], // Southwest corner
    [45.7, -73.4]  // Northeast corner
  ];

  const handleMarkerHover = (pin, event) => {
    if (isMobile) return; // No hover on mobile
    
    setHoveredPin(pin); // For side panel
    
    // Clear any existing timeout
    if (hoverTimeout) {
      clearTimeout(hoverTimeout);
      setHoverTimeout(null);
    }
  };

  const handlePopupExplore = (pinId) => {
    navigate(`/location/${pinId}`);
  };

  const handleMarkerClick = (pin) => {
    if (!isMobile) {
      // Desktop: fade out map then navigate
      const mapElement = document.querySelector('.desktop-layout, .mobile-layout');
      if (mapElement) {
        mapElement.style.transition = 'opacity 0.3s ease-out';
        mapElement.style.opacity = '0';
        setTimeout(() => {
          navigate(`/location/${pin.id}`);
        }, 300);
      } else {
        navigate(`/location/${pin.id}`);
      }
    }
    // Mobile: popup is handled by Leaflet automatically
  };

  const handleSideNavigation = (pinId) => {
    // Desktop: fade out map then navigate
    const mapElement = document.querySelector('.desktop-layout, .mobile-layout');
    if (mapElement) {
      mapElement.style.transition = 'opacity 0.3s ease-out';
      mapElement.style.opacity = '0';
      setTimeout(() => {
        navigate(`/location/${pinId}`);
      }, 300);
    } else {
      navigate(`/location/${pinId}`);
    }
  };

  const handleMapClick = () => {
    // Close any open popups on map click - handled by Leaflet automatically
  };

  // Debounced viewport change handler to reduce re-renders
  const viewportChangeTimeoutRef = useRef(null);

  const handleViewportChange = useCallback((mapInstance) => {
    if (!mapInstance) return;

    // Clear existing timeout
    if (viewportChangeTimeoutRef.current) {
      clearTimeout(viewportChangeTimeoutRef.current);
    }

    // Debounce viewport changes by 150ms
    viewportChangeTimeoutRef.current = setTimeout(() => {
      const newZoom = Math.floor(mapInstance.getZoom());
      const newBounds = mapInstance.getBounds();

      // Only update if zoom actually changed to reduce re-renders
      setZoom(prevZoom => {
        if (prevZoom !== newZoom) {
          console.log('Zoom changed:', prevZoom, '->', newZoom);
          return newZoom;
        }
        return prevZoom;
      });
      setBounds(newBounds);
    }, 150);
  }, []);

  // Cleanup viewport change timeout
  useEffect(() => {
    return () => {
      if (viewportChangeTimeoutRef.current) {
        clearTimeout(viewportChangeTimeoutRef.current);
      }
    };
  }, []);

  const handleClusterClick = (cluster) => {
    // Return the expansion zoom for the ClusterLayer to use
    return supercluster.getClusterExpansionZoom(cluster.id);
  };

  // Set up global function for marker clicks
  useEffect(() => {
    window.navigateToPin = (pinId) => {
      console.log('Global function called for pin:', pinId);
      navigate(`/location/${pinId}`);
    };

    return () => {
      // Clean up global function
      if (window.navigateToPin) {
        delete window.navigateToPin;
      }
    };
  }, [navigate]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // Clear all timeouts and cleanup
      if (hoverTimeout) {
        clearTimeout(hoverTimeout);
      }
      if (viewportChangeTimeoutRef.current) {
        clearTimeout(viewportChangeTimeoutRef.current);
      }
      // Reset clusters to free memory
      setClusters([]);
    };
  }, []);


  if (isMobile) {
    // Mobile layout: full screen map with modals
    return (
      <div className="mobile-layout">
        <MapContainer
          center={getMapCenter()}
          zoom={13}
          minZoom={10}
          maxZoom={18}
          maxBounds={montrealBounds}
          maxBoundsViscosity={1.0}
          style={{ height: 'calc(100vh - 50px)', width: '100vw' }}
        >
          <MapEventHandler 
            onMapClick={handleMapClick} 
            onViewportChange={handleViewportChange}
          />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <ClusterLayer
            clusters={clusters}
            pins={pins}
            zoom={zoom}
            onMarkerClick={handleMarkerClick}
            onMarkerHover={() => {}} // No hover on mobile
            isMobile={true}
            onClusterClick={handleClusterClick}
            onPopupExplore={handlePopupExplore}
          />
          
        </MapContainer>
        
      </div>
    );
  }

  // Desktop layout: map with side panel
  return (
    <div className="desktop-layout">
      <div className="desktop-map-container">
        <MapContainer
          center={getMapCenter()}
          zoom={13}
          minZoom={10}
          maxZoom={18}
          maxBounds={montrealBounds}
          maxBoundsViscosity={1.0}
          style={{ height: 'calc(100vh - 50px)', width: '100%' }}
        >
          <MapEventHandler 
            onMapClick={handleMapClick} 
            onViewportChange={handleViewportChange}
          />
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          <ClusterLayer
            clusters={clusters}
            pins={pins}
            zoom={zoom}
            onMarkerClick={handleMarkerClick}
            onMarkerHover={handleMarkerHover}
            isMobile={false}
            onClusterClick={handleClusterClick}
            onPopupExplore={handlePopupExplore}
          />
          
        </MapContainer>
      </div>
      
      <SidePanel pin={hoveredPin} onNavigate={handleSideNavigation} />
    </div>
  );
};

export default MontrealMap;